import {
  findQuestion,
  isAnswered,
  memberCheckLines,
  screensLeftInModule,
  visibleQuestions,
  type Answer,
  type AnswerInput,
  type ClientBriefResponse,
  type ClientSessionResponse,
  type ConfirmInput,
  type ConsentStep,
  type ContactInput,
  type EngineState,
  type FollowupAnswerInput,
  type FollowupView,
  type Lang,
  type SessionEvent,
  type SessionStatus,
  type Track,
} from '@aera/schema';
import { EASE } from '@aera/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { LazyBriefDocument, LazyBriefSections } from '../brief/lazy.js';
import { AeraMark, Button, Spinner } from '../components/ui.js';
import { applyLang } from '../i18n/index.js';
import { ApiError, clientApi } from '../lib/api.js';
import { permanent, useAutosave } from '../lib/autosave.js';
import { useSessionEvents } from '../lib/sse.js';
import { useTrackTheme } from '../lib/theme.js';
import { PrivacyChip, TopBar } from './Chrome.js';
import { ConsentScreen, DeclinedScreen } from './ConsentScreen.js';
import { buildSteps, nextStepId, openFloorQuestionId, prevStepId, RESUME_MARK, resumeStepId, stepForQuestion, type Step } from './flow.js';
import { FOLLOWUP_POLL_MS, Followups } from './Followups.js';
import { Generating, initialGeneration, progressAfter, type GenerationState } from './Generating.js';
import { Interstitial } from './Interstitial.js';
import { MemberCheck } from './MemberCheck.js';
import { PrivacySheet } from './PrivacySheet.js';
import { QuestionScreen } from './QuestionScreen.js';
import { isEmailValid } from './questions/ContactQuestion.js';
import { Welcome } from './Welcome.js';

const LISTENING: SessionStatus[] = ['followups', 'generating'];

/** How often the follow-up step tries /complete again while it did not get through. */
export const COMPLETE_RETRY_MS = 3000;

/** Saved contact drops an email that is still being typed, so autosave never sends an invalid one. */
const contactPayload = (c: ContactInput): ContactInput => {
  const out: ContactInput = {};
  for (const [k, v] of Object.entries(c) as [keyof ContactInput, string | undefined][]) {
    if (v === undefined) continue;
    if (k === 'email' && v && !isEmailValid(v)) continue;
    out[k] = v;
  }
  return out;
};

export function QuestionnairePage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();

  const [data, setData] = useState<ClientSessionResponse | null>(null);
  const [loadError, setLoadError] = useState<'invalid' | 'error' | null>(null);
  // The browser's language (i18n starts in it) until a session says otherwise: an invalid or expired link loads none.
  const [lang, setLang] = useState<Lang>(() => (i18n.language === 'en' ? 'en' : 'zh'));
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [contact, setContact] = useState<ContactInput | null>(null);
  const [contactConsent, setContactConsent] = useState(false);
  const [stepId, setStepId] = useState<string>('welcome');
  // Whether a screen change has happened since the page loaded: only then does a consent or decline
  // screen move focus to its heading (S12).
  const [moved, setMoved] = useState(false);
  const [followups, setFollowups] = useState<FollowupView[]>([]);
  const [followupsReady, setFollowupsReady] = useState(false);
  const [gen, setGen] = useState<GenerationState>(initialGeneration);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // The language the server holds: a switch it refused goes back to it.
  const serverLang = useRef<Lang>('zh');
  // An answer the server refused (not a network failure: those are retried) is not passed over in silence.
  // A 410 means the link expired, or the session was deleted, while the page was open: nothing more can be
  // saved, so the page says so at once instead of asking for the answer again.
  // save and flush keep their identity across renders, so the callbacks and effects that use them do not
  // change, or re-run, on every render.
  const { save, flush, status: saveStatus } = useAutosave({
    onRejected: (key, err) => {
      if (err instanceof ApiError && err.status === 410) setLoadError('invalid');
      else if (key === 'lang') {
        const kept = serverLang.current;
        setLang(kept);
        langRef.current = kept;
        applyLang(kept);
        setActionError(t('common.langLocked', { lng: kept }));
      } else if (key.startsWith('a:')) setActionError(t('common.saveRejected'));
    },
  });
  const [announce, setAnnounce] = useState('');
  const progressRef = useRef(0);

  // Navigation reads the latest answers synchronously: an answer recorded on Continue changes which
  // screen comes next (C2 appears once C1 has a pick) before React has re-rendered.
  const answersRef = useRef(answers);
  const dataRef = useRef(data);
  const stepRef = useRef(stepId);
  const langRef = useRef(lang);
  dataRef.current = data;
  stepRef.current = stepId;
  langRef.current = lang;

  const questionnaire = data?.questionnaire ?? null;
  const session = data?.session ?? null;
  const status = session?.status ?? 'invited';
  const track: Track | null = session?.track ?? questionnaire?.track ?? null;
  useTrackTheme(track);

  const engineFor = useCallback(
    (a: Record<string, Answer>): EngineState | null => (questionnaire ? { questionnaire, answers: a, lang: langRef.current } : null),
    [questionnaire],
  );
  const engine = useMemo<EngineState | null>(() => (questionnaire ? { questionnaire, answers, lang } : null), [questionnaire, answers, lang]);
  const stepsFor = useCallback(
    (state: EngineState | null): Step[] => (state ? buildSteps(state, { memberCheck: true }) : [{ id: 'welcome', kind: 'welcome' }]),
    [],
  );
  const steps = useMemo(() => stepsFor(engine), [engine, stepsFor]);
  const step = steps.find((s) => s.id === stepId) ?? (stepId === 'declined' ? ({ id: 'declined', kind: 'declined' } as Step) : steps[0]!);

  const setStatus = (next: SessionStatus) => setData((d) => (d ? { ...d, session: { ...d.session, status: next } } : d));

  // The follow-ups are written once, in the session language, and the briefs follow them: from /complete
  // on the API refuses another language (409 lang_locked), so the toggle says so instead of switching.
  const langLocked = !(['invited', 'in_progress', 'declined'] as SessionStatus[]).includes(status) || followups.length > 0;
  const langLockedRef = useRef(langLocked);
  langLockedRef.current = langLocked;

  /* ------------------------------------------------------------------ *
   * Loading and resume
   * ------------------------------------------------------------------ */

  const apply = useCallback(
    (res: ClientSessionResponse, opts: { resume: boolean }) => {
      setData(res);
      answersRef.current = res.answers;
      setAnswers(res.answers);
      setContact(res.contact);
      setContactConsent(res.session.consent.contact);
      setFollowups(res.session.followups);
      setFollowupsReady(res.session.followupsReady);
      setLang(res.session.lang);
      langRef.current = res.session.lang;
      serverLang.current = res.session.lang;
      applyLang(res.session.lang);
      if (res.session.status === 'awaiting_review') setGen((g) => ({ ...g, phase: 'review' }));
      if (res.session.status === 'failed') setGen((g) => ({ ...g, phase: 'failed' }));
      if (opts.resume) {
        if (!res.questionnaire) setStepId('welcome');
        else {
          const state: EngineState = { questionnaire: res.questionnaire, answers: res.answers, lang: res.session.lang };
          setStepId(resumeStepId(stepsFor(state), res.session, res.questionnaire));
        }
      }
    },
    [stepsFor],
  );

  const refresh = useCallback(async () => {
    try {
      const res = await clientApi.session(token);
      setData(res);
      // A response read before the follow-up job saved must not undo the follow-ups the stream has
      // already delivered: the server only ever goes from not ready to ready.
      if (res.session.followupsReady) {
        setFollowups(res.session.followups);
        setFollowupsReady(true);
      }
      if (res.session.status === 'awaiting_review') setGen((g) => ({ ...g, phase: 'review' }));
      if (res.session.status === 'failed') setGen((g) => ({ ...g, phase: 'failed' }));
      if (res.session.status === 'complete' && res.session.briefToken) {
        setGen((g) => ({ ...g, phase: 'done', briefToken: res.session.briefToken }));
      }
      return res;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    clientApi
      .session(token)
      .then((res) => !cancelled && apply(res, { resume: true }))
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError && [400, 401, 403, 404, 410].includes(err.status) ? 'invalid' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [token, apply]);

  /* ------------------------------------------------------------------ *
   * Navigation
   * ------------------------------------------------------------------ */

  const go = useCallback(
    (id: string | null) => {
      if (!id) return;
      setActionError(null);
      const from = stepRef.current;
      // Updated now, not on the next render, so a second press in the same tick is seen as stale.
      stepRef.current = id;
      setStepId(id);
      setMoved(true);
      if (typeof window !== 'undefined') window.scrollTo?.({ top: 0 });
      const target = stepsFor(engineFor(answersRef.current)).find((s) => s.id === id);
      if (target?.kind === 'followups' || target?.kind === 'memberCheck') {
        const mark = RESUME_MARK[target.kind];
        save('patch', (signal) => clientApi.patch(token, { currentQuestionId: mark }, signal), 300);
      }
      if (target?.kind === 'screen') {
        const questionId = target.screen.questionIds[0]!;
        save('patch', (signal) => clientApi.patch(token, { currentQuestionId: questionId }, signal), 300);
        const prev = stepsFor(engineFor(answersRef.current)).find((s) => s.id === from);
        if (prev?.kind !== 'screen' || prev.screen.module !== target.screen.module) {
          const mod = questionnaire?.modules.find((m) => m.id === target.screen.module);
          const index = (questionnaire?.modules.findIndex((m) => m.id === target.screen.module) ?? 0) + 1;
          if (mod) setAnnounce(t('question.announce', { part: t('question.moduleOf', { n: index, total: questionnaire!.modules.length }), title: mod.title[langRef.current] }));
        }
      }
    },
    [engineFor, questionnaire, save, stepsFor, t, token],
  );

  const complete = useCallback(async () => {
    // The follow-ups are written from the open floor: not before the answers have reached the server.
    const unsaved = await flush();
    if (unsaved.length) throw new Error(`unsaved: ${unsaved.join(', ')}`);
    await clientApi.complete(token);
    setStatus('followups');
    void refresh();
  }, [flush, refresh, token]);

  // Each screen moves on once. The screen fading out stays mounted and clickable for its exit (and its
  // Enter listener and the interstitial's timer keep running), so a second Continue, Enter or tap names
  // the step it was rendered for and is ignored once that step is no longer current.
  const advancing = useRef(false);
  // The member check is reached without the follow-ups only once the wait for them ran out (also before a
  // reload there); set then, it lets forward navigation pass over the waiting screen.
  const followupWaitOver = useRef(false);
  const followupsReadyRef = useRef(followupsReady);
  followupsReadyRef.current = followupsReady;
  const next = useCallback(
    async (fromId: string) => {
      const current = dataRef.current;
      if (!current || fromId !== stepRef.current || advancing.current) return;
      const all = stepsFor(engineFor(answersRef.current));
      const here = all.find((s) => s.id === fromId);
      const openFloor = current.questionnaire ? openFloorQuestionId(current.questionnaire) : null;
      if (here?.kind === 'screen' && openFloor && here.screen.questionIds.includes(openFloor) && current.session.status === 'in_progress') {
        advancing.current = true;
        setBusy(true);
        try {
          await complete();
        } catch {
          // The follow-up step retries /complete; the client is not held on this screen.
        } finally {
          advancing.current = false;
          setBusy(false);
        }
        if (fromId !== stepRef.current) return;
      }
      let target = nextStepId(all, fromId, current.session);
      // Once the wait for the follow-ups ran out, forward passes over them until they arrive (Back from
      // the contact screens to fix an answer must not mean another 30 s of waiting).
      if (fromId === 'followups' && !followupsReadyRef.current) followupWaitOver.current = true;
      if (target === 'followups' && followupWaitOver.current && !followupsReadyRef.current) target = nextStepId(all, 'followups', current.session);
      // A member check with no lines would only confirm itself and move on: it is passed over, never flashed.
      const state = engineFor(answersRef.current);
      if (target === 'memberCheck' && state && memberCheckLines(state).length === 0) target = nextStepId(all, 'memberCheck', current.session);
      go(target);
    },
    [complete, engineFor, go, stepsFor],
  );

  // Back from the member check passes over the follow-ups when there is nothing to ask there: none were
  // added, or none have arrived (the member check is only reached past them once the wait ran out, also
  // before a reload). That screen would only wait and move forward again; the stream still delivers late ones.
  // Back from the contact screens likewise passes over a member check with no lines: it would confirm
  // itself and send the client straight back to I4.
  const back = useCallback(
    (fromId: string) => {
      const current = dataRef.current;
      if (!current || fromId !== stepRef.current) return;
      const state = engineFor(answersRef.current);
      const skip = new Set<string>();
      if (!followupsReady || followups.length === 0) skip.add('followups');
      if (state && memberCheckLines(state).length === 0) skip.add('memberCheck');
      go(prevStepId(stepsFor(state), fromId, current.session, skip));
    },
    [engineFor, followups.length, followupsReady, go, stepsFor],
  );

  /* ------------------------------------------------------------------ *
   * Answers, contact, consent, language
   * ------------------------------------------------------------------ */

  const onAnswer = useCallback(
    (questionId: string, input: AnswerInput, opts?: { debounce?: boolean }) => {
      if (!questionnaire) return;
      const q = findQuestion(questionnaire, questionId);
      if (!q) return;
      const answer: Answer = { ...input, questionId, questionVersion: q.version, type: q.type };
      answersRef.current = { ...answersRef.current, [questionId]: answer };
      setAnswers(answersRef.current);
      // A required question left unanswered (its Other text cleared) is not sent: the server refuses it. The
      // no-op takes the place of a partial text still waiting, and Continue holds the client on the screen.
      const empty = q.required && !isAnswered(answer);
      save(`a:${questionId}`, empty ? async () => undefined : (signal) => clientApi.answer(token, questionId, input, signal), opts?.debounce ? 700 : 0);
    },
    [questionnaire, save, token],
  );

  const onContact = useCallback(
    (questionId: string, next: ContactInput) => {
      setContact(next);
      save(`a:${questionId}`, (signal) => clientApi.answer(token, questionId, contactPayload(next), signal), 700);
    },
    [save, token],
  );

  const onContactConsent = useCallback(
    (questionId: string, agree: boolean) => {
      setContactConsent(agree);
      save(`a:${questionId}`, (signal) => clientApi.answer(token, questionId, { agree }, signal));
    },
    [save, token],
  );

  const onLang = useCallback(
    (next: Lang) => {
      if (next === langRef.current) return;
      if (langLockedRef.current) {
        setActionError(t('common.langLocked'));
        return;
      }
      setLang(next);
      langRef.current = next;
      applyLang(next);
      save('lang', async (signal) => {
        const res = await clientApi.patch(token, { lang: next }, signal);
        serverLang.current = next;
        return res;
      });
    },
    [save, t, token],
  );

  const onPickTrack = async (picked: Track) => {
    setBusy(true);
    try {
      await clientApi.patch(token, { track: picked, lang: langRef.current });
      const res = await clientApi.session(token);
      apply(res, { resume: false });
      const state: EngineState | null = res.questionnaire ? { questionnaire: res.questionnaire, answers: res.answers, lang: res.session.lang } : null;
      go(nextStepId(stepsFor(state), 'welcome', res.session));
    } catch {
      setActionError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const onStart = () => {
    if (!data?.questionnaire) return;
    const resume = resumeStepId(steps, data.session, data.questionnaire);
    go(resume !== 'welcome' ? resume : nextStepId(steps, 'welcome', data.session));
  };

  const onConsent = async (kind: ConsentStep, accepted: boolean, textVersion: string) => {
    if (!data) return;
    setBusy(true);
    try {
      await clientApi.consent(token, { step: kind, accepted, textVersion });
      const consent = { ...data.session.consent, [kind]: accepted, declined: kind === 'crossBorder' ? !accepted : data.session.consent.declined };
      const nextStatus: SessionStatus = !accepted ? 'declined' : kind === 'crossBorder' && data.session.status === 'invited' ? 'in_progress' : data.session.status === 'declined' ? 'in_progress' : data.session.status;
      const session = { ...data.session, consent, status: nextStatus };
      setData({ ...data, session });
      if (!accepted) go('declined');
      else go(nextStepId(steps, kind === 'processing' ? 'consent:processing' : 'consent:crossBorder', session));
    } catch {
      setActionError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------ *
   * Follow-ups, member check, generation
   * ------------------------------------------------------------------ */

  // A session that never reached /complete (the connection dropped as the client left the open floor, or a
  // resumed one) catches up here. The follow-up step comes straight after the open floor, so a call that
  // did not get through is tried again, every few seconds and as soon as the browser is back online, for
  // as long as the step waits for the follow-ups; a refusal from the server (a 4xx that retrying will not
  // change) is final and the step moves on. One call at a time, also across a re-run of the effect.
  const completeInFlight = useRef(false);
  useEffect(() => {
    if (step.kind !== 'followups' || status !== 'in_progress') return undefined;
    let stopped = false;
    const attempt = () => {
      if (stopped || completeInFlight.current) return;
      completeInFlight.current = true;
      complete()
        .catch((err: unknown) => {
          if (permanent(err) && !stopped) {
            stopped = true;
            setFollowupsReady(true);
          }
        })
        .finally(() => {
          completeInFlight.current = false;
        });
    };
    attempt();
    const id = setInterval(attempt, COMPLETE_RETRY_MS);
    window.addEventListener('online', attempt);
    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener('online', attempt);
    };
  }, [step.kind, status, complete]);

  // The stream can miss the follow-ups event (it fired while the stream was connecting), so the
  // waiting screen also asks the server now and then; refresh() only ever moves the follow-ups forward.
  // The member check reached after the wait ran out keeps asking, so late ones are there on Back.
  useEffect(() => {
    if ((step.kind !== 'followups' && step.kind !== 'memberCheck') || followupsReady || status !== 'followups') return undefined;
    const id = setInterval(() => void refresh(), FOLLOWUP_POLL_MS);
    return () => clearInterval(id);
  }, [step.kind, followupsReady, status, refresh]);

  const onFollowup = useCallback(
    async (id: string, input: FollowupAnswerInput) => {
      await clientApi.followup(token, id, input);
      setFollowups((list) => list.map((f) => (f.id === id ? { ...f, answer: { selected: input.selected, ...(input.other ? { other: input.other } : {}), ...(input.text ? { text: input.text } : {}), skipped: input.skipped } } : f)));
    },
    [token],
  );

  useEffect(() => {
    if (step.kind !== 'memberCheck') return;
    if (!followupsReadyRef.current) followupWaitOver.current = true;
    void refresh();
  }, [step.kind, refresh]);

  // Built here in the current language (the toggle still switches it when /complete did not go through),
  // with the decisions the server already holds merged by key: the same lines the API builds (memberCheckFor).
  const memberLines = useMemo(() => {
    if (!engine) return session?.memberCheck ?? [];
    const stored = new Map((session?.memberCheck ?? []).map((l) => [l.key, l]));
    return memberCheckLines(engine).map((line) => {
      const prev = stored.get(line.key);
      return prev ? { ...line, confirmed: prev.confirmed, ...(prev.correction ? { correction: prev.correction } : {}) } : line;
    });
  }, [session?.memberCheck, engine]);

  const onConfirm = useCallback(
    async (input: ConfirmInput) => {
      if (stepRef.current !== 'memberCheck') return;
      const unsaved = await flush();
      if (unsaved.length) throw new Error(`unsaved: ${unsaved.join(', ')}`);
      if (input.lines.length) {
        // Kept, so Back from the contact screens shows the decisions and corrections just sent.
        const res = await clientApi.confirm(token, input);
        if (res.memberCheck) setData((d) => (d ? { ...d, session: { ...d.session, memberCheck: res.memberCheck } } : d));
      }
      if (stepRef.current !== 'memberCheck') return;
      go(nextStepId(stepsFor(engineFor(answersRef.current)), 'memberCheck', dataRef.current!.session));
    },
    [engineFor, flush, go, stepsFor, token],
  );

  // /finish that did not get through (network, 5xx, answers not saved yet) is not the server's 'failed':
  // nothing was queued, so the screen says so and offers to try again rather than promising a call.
  const finishing = useRef(false);
  // A page opened mid-generation reads the finished session once: refresh() re-renders, which re-runs the effect.
  const completeRefreshed = useRef(false);
  const [finishAttempt, setFinishAttempt] = useState(0);
  const retryFinish = useCallback(() => {
    finishing.current = false;
    setGen((g) => (g.phase === 'unsent' ? { ...g, phase: 'starting' } : g));
    setFinishAttempt((n) => n + 1);
  }, []);
  useEffect(() => {
    if (step.kind !== 'generating' || finishing.current) return;
    if (status === 'complete') {
      if (!completeRefreshed.current) {
        completeRefreshed.current = true;
        void refresh();
      }
      return;
    }
    if (status !== 'in_progress' && status !== 'followups') return;
    finishing.current = true;
    setGen((g) => ({ ...g, phase: 'internal' }));
    progressRef.current = 0.05;
    void (async () => {
      try {
        const unsaved = await flush();
        if (unsaved.length) throw new Error(`unsaved: ${unsaved.join(', ')}`);
        const res = await clientApi.finish(token);
        setStatus(res.status ?? 'generating');
      } catch (err) {
        // finishing stays set: only the retry button, or coming back online, sends /finish again.
        progressRef.current = 0;
        if (err instanceof ApiError && err.code === 'required_missing') {
          // Same check as the server: the first required question still without an answer.
          const state = engineFor(answersRef.current);
          const missing = state ? visibleQuestions(state).find((q) => q.required && !isAnswered(state.answers[q.id])) : undefined;
          const target = state && missing ? stepForQuestion(stepsFor(state), missing.id) : undefined;
          if (target) {
            finishing.current = false;
            setGen(initialGeneration());
            go(target.id);
            return;
          }
        }
        setGen((g) => ({ ...g, phase: 'unsent' }));
      }
    })();
  }, [step.kind, status, flush, refresh, token, finishAttempt, engineFor, go, stepsFor]);

  // Back online: try again without waiting for a tap.
  useEffect(() => {
    if (gen.phase !== 'unsent') return undefined;
    window.addEventListener('online', retryFinish);
    return () => window.removeEventListener('online', retryFinish);
  }, [gen.phase, retryFinish]);

  // Under the review gate the client brief is the consultant's to release: the streamed draft is never
  // shown, only the progress. Without the gate the text streams in, but only while the stream is known to
  // have seen it from the start (the server keeps no replay): after a reconnect, or when the page opened
  // mid-stream, the screen waits for brief_done rather than showing text with a gap in it.
  const gated = data?.config.reviewGate ?? true;
  const gatedRef = useRef(gated);
  gatedRef.current = gated;
  const streamSynced = useRef(false);
  const onReopen = useCallback(() => {
    streamSynced.current = false;
    setGen((g) => (g.phase === 'done' ? g : { ...g, markdown: '' }));
  }, []);

  const onEvent = useCallback((event: SessionEvent) => {
    switch (event.type) {
      case 'status':
        setStatus(event.status);
        if (event.status === 'awaiting_review') {
          progressRef.current = 1;
          setGen((g) => ({ ...g, phase: 'review' }));
        } else if (event.status === 'failed') setGen((g) => ({ ...g, phase: 'failed' }));
        else if (event.status === 'complete') void refresh();
        break;
      case 'followups':
        setFollowups(event.followups);
        setFollowupsReady(true);
        break;
      case 'progress':
        progressRef.current = progressAfter(progressRef.current, event);
        // Seen before the client brief starts, so the deltas that follow are the whole text.
        if (event.phase === 'internal') streamSynced.current = true;
        if (event.phase !== 'followups') {
          setGen((g) => (g.phase === 'done' || g.phase === 'review' || g.phase === 'failed' ? g : { ...g, phase: event.phase === 'client' ? 'client' : 'internal' }));
        }
        break;
      case 'brief_delta':
        if (gatedRef.current || !streamSynced.current) break;
        setGen((g) => ({ ...g, phase: 'client', markdown: g.markdown + event.text }));
        break;
      case 'brief_reset':
        streamSynced.current = true;
        setGen((g) => ({ ...g, markdown: '' }));
        break;
      case 'brief_done':
        progressRef.current = 1;
        setStatus('complete');
        setGen((g) => ({ ...g, phase: 'done', markdown: event.markdown, briefToken: event.briefToken }));
        break;
      case 'awaiting_review':
        // The snapshot also sends this for a failed session; the failure message stays.
        progressRef.current = 1;
        setData((d) => (d && d.session.status === 'failed' ? d : d ? { ...d, session: { ...d.session, status: 'awaiting_review' } } : d));
        setGen((g) => (g.phase === 'failed' ? g : { ...g, phase: 'review' }));
        break;
      case 'error':
        if (event.code === 'failed') setGen((g) => ({ ...g, phase: 'failed' }));
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const listening = !loadError && !!session && LISTENING.includes(status) && gen.phase !== 'done' && gen.phase !== 'review';
  const { connected } = useSessionEvents(listening ? clientApi.eventsUrl(token) : null, onEvent, () => void refresh(), onReopen);
  useEffect(() => setGen((g) => ({ ...g, reconnecting: listening && !connected && g.phase !== 'starting' && g.phase !== 'unsent' })), [listening, connected]);

  // The tab title follows the language like the rest of the page (index.html carries the zh one).
  useEffect(() => {
    if (typeof document !== 'undefined') document.title = t('common.appName', { lng: lang });
  }, [lang, t]);

  // Once the brief is written, the generating screen hands over to the brief. go is read through a ref: it
  // changes with each session read, which must not start the 900 ms again.
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    if (step.kind !== 'generating' || gen.phase !== 'done' || !gen.briefToken) return undefined;
    const id = setTimeout(() => goRef.current('brief'), 900);
    return () => clearTimeout(id);
  }, [step.kind, gen.phase, gen.briefToken]);

  /* ------------------------------------------------------------------ *
   * Render
   * ------------------------------------------------------------------ */

  if (loadError) {
    return (
      <Centered>
        <h1 className="max-w-[420px] text-center text-[16px] text-ink-2">{loadError === 'invalid' ? t('session.invalid') : t('common.error')}</h1>
        {loadError === 'error' && (
          <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        )}
      </Centered>
    );
  }
  if (!data) {
    return (
      <Centered>
        <Spinner label={t('common.loading')} />
      </Centered>
    );
  }
  if (status === 'anonymised') {
    return (
      <Centered>
        <h1 className="text-ink-2">{t('session.anonymised')}</h1>
      </Centered>
    );
  }

  // Only with the guiding consultant signed in on this device: a client alone on a guided link ticks the
  // boxes themselves (recorded as their own, not verbal, consent), so they get the self-serve wording.
  const guided = data.session.guidedByConsultant;
  const config = data.config;
  const openPrivacy = () => setPrivacyOpen(true);
  const deleteRequest = () => clientApi.deleteRequest(token);

  const topBar = (() => {
    if (!questionnaire) return null;
    if (step.kind === 'screen') {
      const modIndex = questionnaire.modules.findIndex((m) => m.id === step.screen.module);
      const mod = questionnaire.modules[modIndex];
      const left = engine ? screensLeftInModule(engine, step.screen.key) : 0;
      return (
        <TopBar
          progress={step.index / step.total}
          moduleLabel={mod?.title[lang]}
          cue={left > 0 ? t('question.left', { count: left }) : t('question.lastInModule')}
          orbFill={(modIndex + 1) / questionnaire.modules.length}
          lang={lang}
          onLang={onLang}
          onPrivacy={openPrivacy}
          announce={announce}
        />
      );
    }
    if (step.kind === 'consent' || step.kind === 'declined') return <TopBar progress={0} lang={lang} onLang={onLang} onPrivacy={openPrivacy} />;
    if (step.kind === 'interstitial') {
      // Where the module just finished left the bar and the orb; the next screen starts from there.
      const i = steps.findIndex((s) => s.id === step.id);
      const done = steps.slice(0, i).reverse().find((s) => s.kind === 'screen');
      const modIndex = questionnaire.modules.findIndex((m) => m.id === step.module);
      return (
        <TopBar
          progress={done?.kind === 'screen' ? (done.index + 1) / done.total : 0}
          orbFill={(modIndex + 1) / questionnaire.modules.length}
          lang={lang}
          onLang={onLang}
          onPrivacy={openPrivacy}
        />
      );
    }
    if (step.kind === 'followups' || step.kind === 'memberCheck') {
      // Where the contact screens that come next will start, so the bar never moves back.
      const i = steps.findIndex((s) => s.id === step.id);
      const upcoming = steps.slice(i).find((s) => s.kind === 'screen');
      return <TopBar progress={upcoming?.kind === 'screen' ? upcoming.index / upcoming.total : 0.99} orbFill={1} lang={lang} onLang={onLang} onPrivacy={openPrivacy} />;
    }
    return null;
  })();

  const body = (() => {
    switch (step.kind) {
      case 'welcome':
        return (
          <Welcome
            lang={lang}
            onLang={onLang}
            track={data.questionnaire ? track : data.session.track}
            onPickTrack={(picked) => void onPickTrack(picked)}
            onStart={onStart}
            resume={Object.values(answers).some((a) => isAnswered(a))}
            guided={guided}
            busy={busy}
          />
        );
      case 'consent':
        return (
          <ConsentScreen
            kind={step.consent}
            lang={lang}
            config={config}
            guided={guided}
            accepted={data.session.consent[step.consent]}
            onAccept={(v) => void onConsent(step.consent, true, v)}
            onDecline={step.consent === 'crossBorder' ? (v) => void onConsent('crossBorder', false, v) : undefined}
            onBack={() => back(step.id)}
            busy={busy}
            moved={moved}
          />
        );
      case 'declined':
        return <DeclinedScreen config={config} onChangeMind={() => go('consent:crossBorder')} moved={moved} />;
      case 'screen':
        return (
          <QuestionScreen
            screen={step.screen}
            state={engine!}
            contact={contact}
            contactConsent={contactConsent}
            sessionCode={data.session.sessionCode}
            saveStatus={saveStatus}
            onAnswer={onAnswer}
            onContact={onContact}
            onContactConsent={onContactConsent}
            onNext={() => void next(step.id)}
            onBack={() => back(step.id)}
            busy={busy}
          />
        );
      case 'interstitial':
        return <Interstitial text={step.text[lang]} onDone={() => void next(step.id)} />;
      case 'followups':
        return (
          <Followups
            followups={followups}
            ready={followupsReady}
            lang={lang}
            onAnswer={onFollowup}
            onDone={() => void next('followups')}
            onBack={() => back('followups')}
          />
        );
      case 'memberCheck':
        return <MemberCheck key={memberLines.map((l) => l.key).join()} lines={memberLines} onConfirm={onConfirm} onBack={() => back('memberCheck')} />;
      case 'generating':
        return (
          <>
            <MinimalHeader lang={lang} onPrivacy={openPrivacy} />
            <Generating
              gen={gen}
              progressRef={progressRef}
              onRetry={retryFinish}
              contactable={contactConsent && (!!contact?.phoneOrWechat?.trim() || (!!contact?.email && isEmailValid(contact.email)))}
            />
          </>
        );
      case 'brief':
        return (
          <BriefStep
            briefToken={gen.briefToken ?? data.session.briefToken}
            streamed={gen.markdown}
            canEmail={contactConsent && !!contact?.email && isEmailValid(contact.email)}
            onSendEmail={() => clientApi.sendBrief(token)}
            onDeleteRequest={deleteRequest}
          />
        );
    }
  })();

  return (
    <div className="flex min-h-[100svh] flex-col">
      {topBar}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step.id}
          className="flex flex-1 flex-col"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.32, ease: EASE }}
        >
          {body}
        </motion.div>
      </AnimatePresence>
      {actionError && (
        <p role="alert" className="fixed inset-x-4 bottom-24 z-30 mx-auto max-w-[600px] rounded-2xl bg-red-50 px-4 py-3 text-[14px] text-red-800 shadow-sm">
          {actionError}
        </p>
      )}
      <PrivacySheet open={privacyOpen} onClose={() => setPrivacyOpen(false)} lang={lang} config={config} onDeleteRequest={deleteRequest} />
    </div>
  );
}

/** A page of its own (loading, an invalid link, an error): the main landmark. */
function Centered({ children }: { children: ReactNode }) {
  return <main className="flex min-h-[100svh] flex-col items-center justify-center px-6">{children}</main>;
}

function MinimalHeader({ lang, onPrivacy }: { lang: Lang; onPrivacy: () => void }) {
  return (
    <header className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 lp-noprint">
      <AeraMark />
      <PrivacyChip lang={lang} onClick={onPrivacy} />
    </header>
  );
}

/** The brief inside the flow: fetched by its own token so the page matches /brief/:token exactly. */
function BriefStep({
  briefToken,
  streamed,
  canEmail,
  onSendEmail,
  onDeleteRequest,
}: {
  briefToken: string | null;
  streamed: string;
  canEmail: boolean;
  onSendEmail: () => Promise<unknown>;
  onDeleteRequest: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [brief, setBrief] = useState<ClientBriefResponse | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!briefToken) return;
    clientApi.brief(briefToken).then(setBrief, () => setFailed(true));
  }, [briefToken]);
  // The text just streamed stays on screen while the brief is fetched, so the page never goes blank.
  const interim = streamed ? (
    <article className="mx-auto w-full max-w-[680px] px-4 pb-16 pt-6 sm:pt-10" aria-busy>
      <div className="mt-6">
        <Suspense fallback={<pre className="whitespace-pre-wrap font-sans text-[16px] leading-relaxed">{streamed}</pre>}>
          <LazyBriefSections markdown={streamed} />
        </Suspense>
      </div>
    </article>
  ) : (
    <Centered>
      <Spinner label={t('common.loading')} />
    </Centered>
  );
  if (!brief) {
    return failed ? (
      <Centered>
        <h1 className="text-ink-2">{t('brief.notFound')}</h1>
      </Centered>
    ) : (
      interim
    );
  }
  const link = `${window.location.origin}/brief/${briefToken}`;
  return (
    <Suspense fallback={interim}>
      <main>
        <LazyBriefDocument brief={brief} actions={{ briefLink: link, ...(canEmail ? { onSendEmail } : {}) }} onDeleteRequest={onDeleteRequest} />
      </main>
    </Suspense>
  );
}

